import React, {
  useMemo,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import { HorizontalFilterBarItemProps } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useFilterStore } from 'apps/sigma2-ui/src/filters-v2/lib/useFilterStore';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { checkShouldApplyFilter } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getEntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineSecondaryFilterDefinitionsForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFn } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineCleanedSecondaryFiltersGivenUpdatedFilterValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determinePrimaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { resetSecondaryFiltersToDefault } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { setPrimaryFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { setSecondaryFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { tryInitializeFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determinePrimaryFilterKeysForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { ClearAllButton } from 'apps/sigma2-ui/src/filters-v2/components/ClearAllButton';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { determineFilterBarItems } from 'apps/sigma2-ui/src/filters-v2/lib/determineFilterBarItems';
import usePrevious from 'apps/sigma2-ui/src/lib/hooks/usePrevious';
import { MetricDefinitionWhiteLabeling } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { SaveAnalysisButton } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { useReports } from 'apps/sigma2-ui/src/reports/hooks/useReports';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { handleDownloadImage } from 'apps/sigma2-ui/src/components/ui/handle-download-image';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { AddToDashboardMenu } from 'apps/sigma2-ui/src/filters-v2/components/AddToDashboardMenu';
import { AddToDashboardButton } from 'apps/sigma2-ui/src/filters-v2/components/AddToDashboardButton';
import { isEntitledToReports } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';

export interface UseFiltersHook {
  /**
   * The items for the filter bar
   */
  items: HorizontalFilterBarItemProps[];

  /**
   * The current primary filter selections.
   */
  primaryFilterSelections: PrimaryFilterSelection[];

  /**
   * The current secondary filter selections.
   */
  secondaryFilterSelections: SecondaryFilterSelection[];

  /**
   * Set the filters from outside the filter bar. When using this method, your onApply callback will be called, however you will get a boolean value indicating it was a programmatic change.
   * @param params
   */
  setFilters: (params: {
    primaryFilterSelections: PrimaryFilterSelection[];
    secondaryFilterSelections: SecondaryFilterSelection[];
    focusArea?: FocusArea;
    shouldPreventAutoSubmit?: boolean;
  }) => void;

  /**
   * Reset the filters to their initial state.
   * @see useOnUserAnalysisCancelCallback
   */
  resetFiltersToInitialState: (
    selectionsToPreserve?: PrimaryFilterSelection[],
  ) => void;

  /**
   * Indicates whether the filter store has been initialized.
   */
  isInitialized: boolean;

  manualApply: () => void;
}

export const useFilters = ({
  feature,
  focusArea,
  onApply,
  disabled,
  onTryInitializePrimaryFilter,
  onTryInitializeSecondaryFilter,
  metricDefinitionWhiteLabeling,
  getCustomVizRequest,
  getElementToDownload,
  analysisName,
  overridePrimaryFilterKeys,
  dashboardGlobalFilters,
}: {
  feature: FiltersFeatureName;
  metricDefinitionWhiteLabeling: MetricDefinitionWhiteLabeling | null;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  onApply?: OnApplyFn;
  disabled?: boolean;
  onTryInitializePrimaryFilter?: (params: {
    primaryFilterKey: PrimaryFilterKey;
    defaultValue: FilterStoreValue;
  }) => FilterStoreValue | null;
  onTryInitializeSecondaryFilter?: (params: {
    secondaryFilterKey: string;
    defaultValue: FilterStoreValue;
  }) => FilterStoreValue | null;
  getCustomVizRequest?: (args: GetCustomVizRequestArgs) => {
    customVizRequest: CustomVizRequest | undefined;
    viewConfiguration?: ViewConfigurationSchema;
  };
  getElementToDownload?: () => HTMLElement | null;
  overridePrimaryFilterKeys?: PrimaryFilterKey[];
  dashboardGlobalFilters?: FilterDefinitionForExtrasArray | null;
}): UseFiltersHook => {
  const store = useFilterStore();

  const language = useLanguage();
  const { myObservation } = useObservation();
  const { myUser } = useUser();
  const { allDimensions, getAllLevelsForDimension, getLevelByColumnValue } =
    useDimensionConfig();
  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();

  const { exportReport } = useReports();

  const showDownloadSingleAnalysisButton = useMemo(
    () =>
      Boolean(
        myObservation?.showDownloadSingleAnalysisButton &&
          isUserAnalysisEnabled &&
          isEntitledToReports({
            features: myObservation?.features ?? [],
          }),
      ),
    [
      myObservation?.showDownloadSingleAnalysisButton,
      myObservation?.features,
      isUserAnalysisEnabled,
    ],
  );

  const { organizationId, databaseId, databaseRevisionId } = useMemo(() => {
    return {
      databaseId: myObservation?.type.databaseId ?? '',
      databaseRevisionId: myObservation?.type.databaseRevisionId ?? '',
      organizationId: myUser?.organization?.id ?? '',
    };
  }, [myObservation, myUser]);

  const [stagingSecondaryFilters, setStagingSecondaryFilters] = useState<
    SecondaryFilterSelection[]
  >([]);

  const [selectedSecondaryFilterKey, setSelectedSecondaryFilterKey] = useState<
    string | null
  >(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const resetStagingSecondaryFilters = useCallback(() => {
    const secondaryFilterSelections = determineSecondaryFilterSelections({
      myObservation,
      feature,
      focusArea,
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
      analysisName,
    });
    setStagingSecondaryFilters([...secondaryFilterSelections]);
  }, [
    databaseId,
    databaseRevisionId,
    feature,
    analysisName,
    focusArea,
    myObservation,
    organizationId,
    store,
  ]);

  const setStagingSecondaryFilter = useCallback(
    ({
      value,
      secondaryFilterKey,
    }: {
      secondaryFilterKey: string;
      value: FilterStoreValue;
    }) => {
      setStagingSecondaryFilters((prev) =>
        determineCleanedSecondaryFiltersGivenUpdatedFilterValue({
          prev,
          value,
          secondaryFilterKey,
          feature,
          focusArea,
          myObservation,
          analysisName,
          dashboardGlobalFilters: dashboardGlobalFilters ?? [],
        }),
      );
    },
    [feature, analysisName, focusArea, myObservation, dashboardGlobalFilters],
  );

  const primaryFilterKeys = useMemo(
    () =>
      overridePrimaryFilterKeys ??
      determinePrimaryFilterKeysForFeature({ feature }),
    [overridePrimaryFilterKeys, feature],
  );

  const onTryInitializePrimaryFilterCb = useCallback(
    ({
      defaultValue,
      primaryFilterKey,
    }: {
      primaryFilterKey: PrimaryFilterKey;
      defaultValue: FilterStoreValue;
    }) => {
      if (onTryInitializePrimaryFilter) {
        return onTryInitializePrimaryFilter({
          primaryFilterKey,
          defaultValue,
        });
      }
      return defaultValue;
    },
    [onTryInitializePrimaryFilter],
  );

  const onTryInitializeSecondaryFilterCb = useCallback(
    ({
      defaultValue,
      secondaryFilterKey,
    }: {
      secondaryFilterKey: string;
      defaultValue: FilterStoreValue;
    }) => {
      if (onTryInitializeSecondaryFilter) {
        return onTryInitializeSecondaryFilter({
          secondaryFilterKey,
          defaultValue,
        });
      }
      return defaultValue;
    },
    [onTryInitializeSecondaryFilter],
  );

  // Check if all primary filters are initialized, which lets the world know we are ready to go.
  const isInitialized = useMemo(() => {
    return primaryFilterKeys.every((primaryFilterKey) => {
      const fullKey = getPrimaryFilterFullKey({ primaryFilterKey });
      const scope = determineFilterStoreScope({
        fullKey,
        focusArea,
        analysisName,
        feature,
        databaseId,
        databaseRevisionId,
        organizationId,
      });
      return scope && store.isInitialized({ scope, key: fullKey });
    });
  }, [
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKeys,
    store,
  ]);

  const resetFiltersToInitialStateCb = useCallback(
    (selectionsToPreserve: PrimaryFilterSelection[] = []) => {
      const newPrimaryFilterSelections: PrimaryFilterSelection[] = [];

      for (const primaryFilterKey of primaryFilterKeys) {
        const fullKey = getPrimaryFilterFullKey({
          primaryFilterKey,
        });

        const scope = determineFilterStoreScope({
          fullKey,
          focusArea,
          analysisName,
          feature,
          databaseId,
          databaseRevisionId,
          organizationId,
        });

        if (!scope || !store.isInitialized({ scope, key: fullKey })) {
          continue;
        }

        newPrimaryFilterSelections.push({
          value:
            selectionsToPreserve.find(
              (selection) => selection.primaryFilterKey === primaryFilterKey,
            )?.value ??
            store.getInitialValue({
              key: fullKey,
              scope,
            }),
          primaryFilterKey,
        });
      }

      setPrimaryFilters({
        selections: newPrimaryFilterSelections,
        databaseId,
        databaseRevisionId,
        feature,
        focusArea,
        analysisName,
        organizationId,
        store,
      });

      const secondaryFilters = determineSecondaryFilterDefinitionsForFeature({
        feature,
        focusArea,
        filterSetDefinitionsWithValues:
          myObservation?.filterSetDefinitionsWithValues ?? [],
        dimensionDefinitions: allDimensions,
        focusAreas: myObservation?.focusAreas ?? [],
        pipelineAnalysisConfiguration:
          myObservation?.pipelineAnalysisConfiguration,
        analysisName,
      });

      const secondaryFilterSelections: SecondaryFilterSelection[] = [];

      for (const secondaryFilter of secondaryFilters) {
        const scope = determineFilterStoreScope({
          fullKey: secondaryFilter.columnHeaderValue,
          focusArea,
          analysisName,
          feature,
          databaseId,
          databaseRevisionId,
          organizationId,
        });

        const fullKey = getSecondaryFilterFullKey({
          secondaryFilterKey: secondaryFilter.columnHeaderValue,
        });

        if (
          !scope ||
          !store.isInitialized({
            scope,
            key: fullKey,
          })
        ) {
          continue;
        }

        secondaryFilterSelections.push({
          value: store.getInitialValue({
            key: fullKey,
            scope,
          }),
          secondaryFilterKey: secondaryFilter.columnHeaderValue,
        });
      }

      setSecondaryFilters({
        store,
        feature,
        focusArea,
        analysisName,
        organizationId,
        databaseRevisionId,
        databaseId,
        myObservation,
        selections: secondaryFilterSelections,
        dashboardGlobalFilters: dashboardGlobalFilters ?? [],
      });
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
      myObservation,
      primaryFilterKeys,
      allDimensions,
      dashboardGlobalFilters,
    ],
  );

  const entitledFilterFeatureDetails = useMemo(() => {
    return getEntitledDetailsInContextOfFiltersFeatureName({
      feature,
      focusArea,
      myObservation,
    });
  }, [feature, focusArea, myObservation]);

  // Any time primaryFilterKeys or secondaryFilterKeys is updated, we should try to initialize all our filters.
  const tryInitializeFiltersCb = useCallback(
    (focusAreaArg: FocusArea | null = null) => {
      return tryInitializeFilters({
        primaryFilterKeys,
        allDimensions,
        entitledFilterFeatureDetails,
        getAllLevelsForDimension,
        feature,
        focusArea: focusAreaArg ?? focusArea,
        databaseId,
        databaseRevisionId,
        organizationId,
        store,
        myObservation,
        onTryInitializePrimaryFilter: onTryInitializePrimaryFilterCb,
        onTryInitializeSecondaryFilter: onTryInitializeSecondaryFilterCb,
        analysisName,
        dashboardGlobalFilters: dashboardGlobalFilters ?? [],
      });
    },
    [
      primaryFilterKeys,
      allDimensions,
      entitledFilterFeatureDetails,
      getAllLevelsForDimension,
      feature,
      focusArea,
      databaseId,
      databaseRevisionId,
      organizationId,
      store,
      myObservation,
      onTryInitializePrimaryFilterCb,
      onTryInitializeSecondaryFilterCb,
      analysisName,
      dashboardGlobalFilters,
    ],
  );

  useEffect(() => {
    tryInitializeFiltersCb();
  }, [tryInitializeFiltersCb]);

  const primaryFilterSelections = useMemo(
    () =>
      determinePrimaryFilterSelections({
        primaryFilterKeys,
        feature,
        focusArea,
        analysisName,
        databaseRevisionId,
        databaseId,
        organizationId,
        store,
      }),
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      primaryFilterKeys,
      store,
    ],
  );

  const secondaryFilterSelections = useMemo(() => {
    return determineSecondaryFilterSelections({
      store,
      feature,
      focusArea,
      organizationId,
      databaseId,
      databaseRevisionId,
      myObservation,
      analysisName,
    });
  }, [
    databaseId,
    databaseRevisionId,
    feature,
    analysisName,
    focusArea,
    myObservation,
    organizationId,
    store,
  ]);

  // Call onApply when primary or secondary filters change
  const programmaticChangeRef = useRef(false);
  const isInitialApplyRef = useRef(true);
  const shouldPreventAutoSubmitRef = useRef(false);

  const previousPrimaryFilterSelections = usePrevious(primaryFilterSelections);
  const previousSecondaryFilterSelections = usePrevious(
    secondaryFilterSelections,
  );

  useEffect(() => {
    const shouldApplyFilter = checkShouldApplyFilter({
      feature,
      primaryFilterSelections,
      previousPrimaryFilterSelections,
      secondaryFilterSelections,
      previousSecondaryFilterSelections,
      shouldPreventAutoSubmit: shouldPreventAutoSubmitRef.current,
    });

    shouldPreventAutoSubmitRef.current = false;

    if (onApply && shouldApplyFilter) {
      const isProgrammaticChange = programmaticChangeRef.current;
      programmaticChangeRef.current = false;

      const isInitialApply = isInitialApplyRef.current;
      isInitialApplyRef.current = false;

      onApply({
        primaryFilterSelections,
        secondaryFilterSelections,
        isProgrammaticChange,
        isInitialApply,
      });
    }
  }, [
    feature,
    focusArea,
    getCustomVizRequest,
    onApply,
    previousPrimaryFilterSelections,
    previousSecondaryFilterSelections,
    primaryFilterSelections,
    secondaryFilterSelections,
  ]);

  const resetSecondaryFiltersToDefaultCb = useCallback(
    (newFocusArea: FocusArea | null = null) => {
      resetSecondaryFiltersToDefault({
        feature,
        focusArea: newFocusArea ?? focusArea,
        organizationId,
        store,
        myObservation,
        databaseId,
        databaseRevisionId,
        analysisName,
      });
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      analysisName,
      focusArea,
      myObservation,
      organizationId,
      store,
    ],
  );

  const clearAllItem: HorizontalFilterBarItemProps = useMemo(
    () => ({
      content: () => (
        <ClearAllButton
          onClick={() => {
            resetSecondaryFiltersToDefaultCb();
          }}
          disabled={Boolean(disabled)}
        />
      ),
      menu: () => <></>,
      key: 'clearAll',
    }),
    [resetSecondaryFiltersToDefaultCb, disabled],
  );

  const saveAnalysisItem: HorizontalFilterBarItemProps = useMemo(
    () => ({
      content: () =>
        getCustomVizRequest && (
          <SaveAnalysisButton
            disabled={Boolean(disabled)}
            getCustomVizRequest={() =>
              getCustomVizRequest &&
              getCustomVizRequest({
                primaryFilterSelections,
                secondaryFilterSelections,
                focusArea,
              })
            }
          />
        ),
      menu: () => <></>,
      key: 'saveAnalysis',
    }),
    [
      disabled,
      focusArea,
      getCustomVizRequest,
      primaryFilterSelections,
      secondaryFilterSelections,
    ],
  );

  const addToDashboardItem: HorizontalFilterBarItemProps = useMemo(
    () => ({
      content: ({ openMenu }) =>
        getCustomVizRequest && (
          <AddToDashboardButton
            disabled={Boolean(disabled)}
            onClick={openMenu}
          />
        ),
      menu: ({ closeMenu }) =>
        getCustomVizRequest && (
          <AddToDashboardMenu
            getCustomVizRequest={() =>
              getCustomVizRequest({
                primaryFilterSelections,
                secondaryFilterSelections,
                focusArea,
              })
            }
            closeMenu={closeMenu}
          />
        ),
      key: 'addToDashboard',
    }),
    [
      disabled,
      focusArea,
      getCustomVizRequest,
      primaryFilterSelections,
      secondaryFilterSelections,
    ],
  );

  /**
   * For some cases one of the filters is the focus area. as the store depends
   * on this focus area there will be a conflict when setting filters. Filters
   * will be applied and saved on the store using the previous focus area.
   * The focus area argument allows us to override this dependency and save
   * filters on the new focus area.
   */
  const setFiltersCb = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      focusArea: localFocusArea,
      shouldPreventAutoSubmit,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
      focusArea?: FocusArea;
      shouldPreventAutoSubmit?: boolean;
    }) => {
      programmaticChangeRef.current = true;
      if (shouldPreventAutoSubmit) {
        // Mark our next useEffect to be aware that our user preference is to supersede the preventAutoSubmit feature.
        shouldPreventAutoSubmitRef.current = true;
      }

      setPrimaryFilters({
        selections: primaryFilterSelections,
        databaseId,
        databaseRevisionId,
        feature,
        focusArea: localFocusArea ?? focusArea,
        analysisName,
        store,
        organizationId,
      });

      setSecondaryFilters({
        selections: secondaryFilterSelections,
        myObservation,
        organizationId,
        store,
        feature,
        focusArea: localFocusArea ?? focusArea,
        databaseRevisionId,
        databaseId,
        analysisName,
        dashboardGlobalFilters: dashboardGlobalFilters ?? [],
      });
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      analysisName,
      focusArea,
      myObservation,
      organizationId,
      store,
      dashboardGlobalFilters,
    ],
  );

  const setPrimaryFiltersCb = useCallback(
    ({
      selections,
      shouldPreventAutoSubmit,
    }: {
      selections: PrimaryFilterSelection[];
      shouldPreventAutoSubmit?: boolean;
    }) => {
      if (shouldPreventAutoSubmit) {
        // Mark our next useEffect to be aware that our user preference is to supersede the preventAutoSubmit feature.
        shouldPreventAutoSubmitRef.current = true;
      }

      setPrimaryFilters({
        selections,
        databaseRevisionId,
        organizationId,
        databaseId,
        feature,
        focusArea,
        analysisName,
        store,
      });
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
    ],
  );

  const setSecondaryFiltersCb = useCallback(
    ({
      selections,
      shouldPreventAutoSubmit,
    }: {
      selections: SecondaryFilterSelection[];
      shouldPreventAutoSubmit?: boolean;
    }) => {
      if (shouldPreventAutoSubmit) {
        // Mark our next useEffect to be aware that our user preference is to supersede the preventAutoSubmit feature.
        shouldPreventAutoSubmitRef.current = true;
      }

      setSecondaryFilters({
        selections,
        databaseRevisionId,
        organizationId,
        databaseId,
        feature,
        focusArea,
        store,
        myObservation,
        analysisName,
        dashboardGlobalFilters: dashboardGlobalFilters ?? [],
      });
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      analysisName,
      focusArea,
      myObservation,
      organizationId,
      store,
      dashboardGlobalFilters,
    ],
  );

  const downloadLegacy = useAsyncCallback(
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || !getElementToDownload) {
        return;
      }
      const $el = getElementToDownload();

      if (!$el || disabled || isDownloading) {
        return;
      }

      setIsDownloading(true);

      const downloadName = `${feature}-${getDateAsYYYYMMDD(new Date())}`;

      await handleDownloadImage($el, downloadName);

      if (!isAlive()) {
        return;
      }

      setIsDownloading(false);
    },
    [getElementToDownload, disabled, isDownloading, feature],
  );

  const downloadCallback = useCallback(() => {
    if (!getCustomVizRequest) {
      return;
    }

    const { customVizRequest } = getCustomVizRequest({
      primaryFilterSelections,
      secondaryFilterSelections,
      focusArea,
    });

    if (!customVizRequest) {
      return;
    }

    if (showDownloadSingleAnalysisButton) {
      setIsDownloading(true);
      exportReport({
        request: { charts: [{ customVizRequest }] },
        organizationId: customVizRequest.organizationId,
        databaseId: customVizRequest.observationLocationType.databaseId,
        databaseRevisionId:
          customVizRequest.observationLocationType.databaseRevisionId,
        onStarted: () => {
          setTimeout(() => {
            setIsDownloading(false);
          }, 3000);
        },
      });
    } else {
      downloadLegacy();
    }
  }, [
    downloadLegacy,
    exportReport,
    getCustomVizRequest,
    focusArea,
    primaryFilterSelections,
    secondaryFilterSelections,
    showDownloadSingleAnalysisButton,
  ]);

  const manualApply = useCallback(() => {
    if (!onApply) {
      return;
    }
    onApply({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange: false,
      isInitialApply: false,
    });
  }, [onApply, primaryFilterSelections, secondaryFilterSelections]);

  return useMemo(() => {
    const customVizRequest = getCustomVizRequest?.({
      primaryFilterSelections,
      secondaryFilterSelections,
      focusArea,
    }).customVizRequest;

    return {
      items: determineFilterBarItems({
        databaseId,
        databaseRevisionId,
        feature,
        focusArea,
        store,
        organizationId,
        clearAllItem,
        saveAnalysisItem,
        addToDashboardItem,
        setStagingSecondaryFilter,
        resetStagingSecondaryFilters,
        stagingSecondaryFilters,
        myObservation,
        setSecondaryFilters: setSecondaryFiltersCb,
        setPrimaryFilters: setPrimaryFiltersCb,
        resetFiltersToInitialState: resetFiltersToInitialStateCb,
        resetSecondaryFiltersToDefault: resetSecondaryFiltersToDefaultCb,
        allDimensions,
        entitledFilterFeatureDetails,
        getAllLevelsForDimension,
        getLevelByColumnValue,
        primaryFilterKeys,
        primaryFilterSelections,
        selectedSecondaryFilterKey,
        setSelectedSecondaryFilterKey,
        metricWhiteLabeling: metricDefinitionWhiteLabeling,
        disabled: Boolean(disabled),
        downloadEnabled:
          Boolean(getElementToDownload) && showDownloadSingleAnalysisButton,
        isDownloading,
        downloadCallback,
        analysisName,
        calibrationFraming: myObservation?.calibrationFraming ?? 'bias',
        language,
        customVizRequest,
        dashboardGlobalFilters: dashboardGlobalFilters ?? [],
      }),
      secondaryFilterSelections,
      primaryFilterSelections,
      setFilters: setFiltersCb,
      resetFiltersToInitialState: resetFiltersToInitialStateCb,
      isInitialized,
      manualApply,
    };
  }, [
    getCustomVizRequest,
    primaryFilterSelections,
    secondaryFilterSelections,
    focusArea,
    databaseId,
    databaseRevisionId,
    feature,
    store,
    organizationId,
    clearAllItem,
    saveAnalysisItem,
    addToDashboardItem,
    setStagingSecondaryFilter,
    resetStagingSecondaryFilters,
    stagingSecondaryFilters,
    myObservation,
    setSecondaryFiltersCb,
    setPrimaryFiltersCb,
    resetFiltersToInitialStateCb,
    resetSecondaryFiltersToDefaultCb,
    allDimensions,
    entitledFilterFeatureDetails,
    getAllLevelsForDimension,
    getLevelByColumnValue,
    primaryFilterKeys,
    primaryFilterSelections,
    selectedSecondaryFilterKey,
    metricDefinitionWhiteLabeling,
    disabled,
    getElementToDownload,
    showDownloadSingleAnalysisButton,
    isDownloading,
    downloadCallback,
    analysisName,
    language,
    dashboardGlobalFilters,
    setFiltersCb,
    isInitialized,
    manualApply,
  ]);
};
